import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FullWidthLayout from '../layouts/FullWidthLayout';
import { ROUTES } from '../config/routes';
import './LoginPage.css';
import Container from '../components/Container';
import * as Api from '../services/api';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import { Avatar } from '@mui/joy';
import { AwesomeQRCode } from '@awesomeqr/react';
import andLogo from '../assets/logo.png';
import { useAppContext } from '../context/AppContext';
import { getDownloadURL, ref } from 'firebase/storage';
import { createTracker } from '../utils/log';
import axios from 'axios';
import { isValidSession } from '../services/jwt';

// TODO: Share this with the other pages
const fsImageAsDataURI = async (path) => {
  const blob = await fetch(path).then((res) => res.blob());
  const reader = new FileReader();
  return await new Promise((resolve, reject) => {
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    return reader.readAsDataURL(blob);
  });
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [showRegister, setShowRegister] = useState(false);
  const [counter, setCounter] = useState(0);
  const [nonceHex, setNonceHex] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState<string>('');
  const { db, storage, andee, setAndee, sessionInfo } = useAppContext();
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const trackEvent = createTracker(andee, sessionInfo);

  useEffect(() => {
    if (state?.path) {
      window.history.replaceState(
        {},
        document.title,
        `${window.location.pathname}?redirect=${state?.path}`
      );
    }
  }, []);

  useEffect(() => {
    async function getSrc() {
      if (storage) {
        try {
          const reference = ref(storage, andee?.profilePicUrl);
          const url = await getDownloadURL(reference);
          setAvatarSrc(url);
        } catch (e) {
          console.debug(e); // suppress child storage reference error
        }
      }
    }
    getSrc();
  }, [storage, andee]);

  useEffect(() => {
    if (!showRegister && counter > 10) {
      setShowRegister(true);
    }
  }, [counter, showRegister]);

  useEffect(() => {
    async function getImageAsDataURI() {
      const dataURI = (await fsImageAsDataURI(andLogo)) as string;
      setBackgroundImage(dataURI);
    }
    getImageAsDataURI();
  }, []);

  useEffect(() => {
    async function getNonce() {
      let nonceHex = new URLSearchParams(window.location.search).get(
        'nonceHex'
      );
      if (!nonceHex) {
        ({ nonceHex } = await Api.getNonce());
      }
      Api.listenForAuthSession(db, nonceHex, setAndee, setLoading);
      setNonceHex(nonceHex as string);
    }
    if (!andee && db) {
      getNonce();
    }
    async function checkIfSessionValid() {
      if (await isValidSession()) {
        setLoading(false);
        if (andee) {
          if (state?.path) {
            navigate(state?.path);
          } else {
            navigate(ROUTES.MARKETPLACE);
          }
          trackEvent('login', 'success');
        }
      } else {
        trackEvent('login', 'failure');
        // delete cookies
        document.cookie = 'andee=; maxAge=0;';
        document.cookie = 'andSessionInfo=; maxAge=0;';
      }
    }
    checkIfSessionValid();
  }, [db, andee, setAndee, sessionInfo]);

  const qrLogin = () => {
    return (
      <>
        <Typography level="body1">
          Please scan the QR Code to authenticate
        </Typography>
        <Box
          sx={{
            width: 300,
            height: 300,
            py: 3,
          }}
        >
          {backgroundImage && (
            <AwesomeQRCode
              options={{
                text: `https://now.andme.org/?a=auth&id=${nonceHex}`,
                margin: 0,
                autoColor: true,
                backgroundImage,
              }}
            />
          )}
        </Box>
      </>
    );
  };

  const interimLoginState = () => (
    <>
      <Typography level="body1">
        <Avatar alt={andee?.firstName} src={avatarSrc} size="lg"></Avatar>
        {andee?.firstName
          ? `Logging you in as ${andee?.firstName}...`
          : 'Logging you in...'}
      </Typography>
    </>
  );

  return (
    <FullWidthLayout>
      <Container>
        <div className={'login__form'}>
          <Typography level="display1" onClick={() => setCounter(counter + 1)}>
            Login
          </Typography>
          {!loading ? qrLogin() : interimLoginState()}
        </div>
      </Container>
    </FullWidthLayout>
  );
};

export default LoginPage;
